import { TagSection } from '@/shared/components/TagSection.tsx';
import { getTagType } from '@/shared/utils/getTagType.ts';
import { PRODUCT_GROUP_TYPE } from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { useBalanceSheetCalculations } from '@/shared/components/ProductGroupHeader/hooks/useBalanceSheetCalculations.ts';
import { FrancoIndicator } from './FrancoIndicator.tsx';
import { BalanceProductPropsWithFallbackValues } from '../context/BalanceSheetContext.tsx';
import { PRODUCT_CATEGORY_LABEL } from '@/phrase/ProductCategoryLabels.ts';

export type ProductGroupBalanceProps = {
    products: Omit<BalanceProductPropsWithFallbackValues, 'id'>[];
    productGroupType: PRODUCT_GROUP_TYPE;
};

export function ProductGroupBalance({ products, productGroupType }: ProductGroupBalanceProps) {
    const { t } = useTranslation();
    const {
        customerPrice,
        customerPriceString,
        materialPrice,
        materialPriceString,
        servicePrice,
        servicePriceString,
        transportPrice,
        transportPriceString,
        takeRate,
        takeRateString,
    } = useBalanceSheetCalculations({
        products,
    });

    return (
        <div className="flex justify-between gap-4 md:gap-6">
            <FrancoIndicator products={products} productGroupType={productGroupType} />
            <div className="flex flex-wrap justify-end gap-6 divide-x">
                <TagSection
                    title={t('product.balanceSheet.client')}
                    label={customerPriceString}
                    type={getTagType({ value: customerPrice })}
                />

                {materialPrice !== null && (
                    <TagSection
                        className="pl-6"
                        title={
                            productGroupType === 'MERCHANT'
                                ? t('product.category.label.material_transport')
                                : t(PRODUCT_CATEGORY_LABEL['MATERIAL'])
                        }
                        label={materialPriceString}
                        type={getTagType({ value: materialPrice, reverseFlag: true })}
                    />
                )}

                {transportPrice !== null && productGroupType !== 'MERCHANT' && (
                    <TagSection
                        className="pl-6"
                        title={t(PRODUCT_CATEGORY_LABEL['TRANSPORT'])}
                        label={transportPriceString}
                        type={getTagType({ value: transportPrice, reverseFlag: true })}
                    />
                )}

                {servicePrice !== null && (
                    <TagSection
                        className="pl-6"
                        title={t(PRODUCT_CATEGORY_LABEL['MATERIAL_SERVICE'])}
                        label={servicePriceString}
                        type={getTagType({ value: servicePrice, reverseFlag: true })}
                    />
                )}

                <TagSection
                    className="pl-6"
                    title={t('product.balanceSheet.takeRate')}
                    label={takeRateString}
                    description={takeRate === null ? '---' : undefined}
                    type={getTagType({ value: takeRate ?? 0 })}
                />
            </div>
        </div>
    );
}
